import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  eventCode?: string;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  eventCode,
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [confirmationInput, setConfirmationInput] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setConfirmationInput('');
    }
  }, [open]);

  const isConfirmationValid = eventCode ? confirmationInput === eventCode : true;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error will be handled by the store
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <DialogContentText>{message}</DialogContentText>
          {eventCode && (
            <>
              <Typography variant="body2" color="textSecondary">
                To confirm, please type the Event Code below:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Event Code"
                placeholder={eventCode}
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                disabled={isDeleting}
                size="small"
              />
              <Typography variant="caption" color="error">
                {confirmationInput && confirmationInput !== eventCode && 'Event Code does not match'}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting || !isConfirmationValid}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
